/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSnackbar } from "hooks/useSnackbar";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import {
  getQueueStats,
  cleanQueue,
  pauseQueue,
  resumeQueue,
} from "services/queueService";
import { QueueOverview, QueueName, QUEUE_LABELS } from "types/queue";
import QueueStatsCard from "./components/QueueStatsCard";
import CleanQueueDialog, {
  PauseQueueDialog,
} from "./components/CleanQueueDialog";

const useStyles = makeStyles((theme: any) => ({
  pageContainer: {
    padding: "12px",
  },
  headerSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 32,
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 28,
    color: "#1d1030",
  },
  headerSubtitle: {
    fontSize: 14,
    color: "#666",
    marginTop: 4,
  },
  headerActions: {
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  refreshButton: {
    minWidth: 40,
    padding: "8px !important",
  },
  lastUpdated: {
    fontSize: 12,
    color: "#666",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
  },
  cardsContainer: {
    marginTop: 16,
  },
}));

const QueueMonitoring: React.FC = () => {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();

  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [queueStats, setQueueStats] = useState<QueueOverview[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Dialog states
  const [cleanDialogOpen, setCleanDialogOpen] = useState(false);
  const [cleanQueueName, setCleanQueueName] = useState<QueueName | null>(null);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pauseQueueName, setPauseQueueName] = useState<QueueName | null>(null);
  const [pauseQueueIsPaused, setPauseQueueIsPaused] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Permissions
  const hasAdminPermission = sessionHelper.hasRole(
    PermittedRoles.QUEUE_MONITORING.ADMIN_ROLES
  );

  // Fetch stats only
  const fetchData = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true);
      else setLoading(true);

      const statsResp = await getQueueStats();

      if (statsResp?.success || statsResp?.status) {
        setQueueStats(statsResp.data);
      }
      setLastUpdated(new Date());
    } catch (error: any) {
      showSnackbar(error.message || "Failed to fetch queue data", "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto refresh every 30 seconds
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Handle pause/resume queue - open dialog
  const handlePauseResumeClick = (queueName: QueueName, isPaused: boolean) => {
    setPauseQueueName(queueName);
    setPauseQueueIsPaused(isPaused);
    setPauseDialogOpen(true);
  };

  // Confirm pause/resume
  const handleConfirmPauseResume = async () => {
    if (!pauseQueueName) return;

    try {
      setActionLoading(true);
      const resp = pauseQueueIsPaused
        ? await resumeQueue(pauseQueueName)
        : await pauseQueue(pauseQueueName);
      if (resp?.success || resp?.status) {
        showSnackbar(
          `Queue ${pauseQueueIsPaused ? "resumed" : "paused"} successfully`,
          "success"
        );
        setPauseDialogOpen(false);
        fetchData(true);
      }
    } catch (error: any) {
      showSnackbar(error.message || "Failed to update queue status", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle clean queue
  const handleOpenCleanDialog = (queueName: QueueName) => {
    setCleanQueueName(queueName);
    setCleanDialogOpen(true);
  };

  const handleCleanQueue = async (
    queueName: QueueName,
    state: "completed" | "failed",
    gracePeriodMs: number
  ) => {
    try {
      setActionLoading(true);
      const resp = await cleanQueue({ queueName, state, gracePeriodMs });
      if (resp?.success || resp?.status) {
        showSnackbar(
          `Cleaned ${resp.data.removed} ${state} jobs from ${QUEUE_LABELS[queueName]}`,
          "success"
        );
        setCleanDialogOpen(false);
        fetchData(true);
      }
    } catch (error: any) {
      showSnackbar(error.message || "Failed to clean queue", "error");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <ContentShell title="Queue Monitoring">
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </ContentShell>
    );
  }

  return (
    <ContentShell title="Queue Monitoring">
      <Box className={classes.pageContainer}>
        {/* Header Section */}
        <Box className={classes.headerSection}>
          <Box>
            <Typography className={classes.headerTitle}>
              Queue Overview
            </Typography>
            <Typography className={classes.headerSubtitle}>
              Monitor and manage BullMQ job queues across RGS and CMS systems
            </Typography>
          </Box>
          <Box className={classes.headerActions}>
            {lastUpdated && (
              <Typography className={classes.lastUpdated}>
                Updated: {lastUpdated.toLocaleTimeString()}
              </Typography>
            )}
            <Tooltip title="Refresh Data">
              <IconButton
                onClick={() => fetchData(true)}
                disabled={refreshing}
                className={classes.refreshButton}
                color="primary"
              >
                {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Queue Stats Cards */}
        <Box className={classes.cardsContainer}>
          <Grid container spacing={3}>
            {queueStats.map((queue) => (
              <Grid item xs={12} md={6} key={queue.queueName}>
                <QueueStatsCard
                  queue={queue}
                  showActions={hasAdminPermission}
                  onPauseResume={handlePauseResumeClick}
                  onClean={handleOpenCleanDialog}
                  actionLoading={actionLoading}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Dialogs */}
        <CleanQueueDialog
          open={cleanDialogOpen}
          onClose={() => setCleanDialogOpen(false)}
          onConfirm={handleCleanQueue}
          queueName={cleanQueueName}
          loading={actionLoading}
        />

        <PauseQueueDialog
          open={pauseDialogOpen}
          onClose={() => setPauseDialogOpen(false)}
          onConfirm={handleConfirmPauseResume}
          queueName={pauseQueueName}
          isPaused={pauseQueueIsPaused}
          loading={actionLoading}
        />
      </Box>
    </ContentShell>
  );
};

export default QueueMonitoring;
